import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Code, Copy, ExternalLink, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function WidgetConfigPage() {
  const [embedData, setEmbedData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/widget/embed-code').then(r => setEmbedData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const copyCode = (code, label) => {
    navigator.clipboard.writeText(code);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-[900px]">
      <div>
        <h1 data-testid="widget-config-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Chat Widget</h1>
        <p className="text-slate-500 mt-1">Embed the AI triage chat on your website</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Live Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-emerald-100 text-emerald-700">Ready to embed</Badge>
            {embedData?.widget_url && (
              <a href={embedData.widget_url} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-600 hover:underline flex items-center gap-1">
                Open Widget <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          <div className="bg-slate-100 rounded-xl p-4 h-[400px] relative overflow-hidden">
            {embedData?.widget_url && (
              <iframe src={embedData.widget_url} className="absolute right-4 bottom-4 w-[360px] h-[380px] rounded-2xl shadow-lg border-0" title="Chat Widget Preview" />
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Code className="w-5 h-5 text-sky-600" /></div>
            <div><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Floating Chat Bubble</CardTitle><p className="text-sm text-slate-500 mt-0.5">Add a chat bubble to your website that opens the triage chat</p></div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-3">Paste this code before the closing <code className="bg-slate-100 px-1 rounded">&lt;/body&gt;</code> tag of your website:</p>
          <div className="relative">
            <pre className="bg-slate-900 text-emerald-400 p-4 rounded-lg text-xs overflow-x-auto max-h-48">{embedData?.bubble_code || 'Loading...'}</pre>
            <Button size="sm" variant="secondary" className="absolute top-2 right-2" onClick={() => copyCode(embedData?.bubble_code, 'Bubble code')} data-testid="copy-bubble-code">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Code className="w-5 h-5 text-emerald-600" /></div>
            <div><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Inline iFrame</CardTitle><p className="text-sm text-slate-500 mt-0.5">Embed the chat directly as an iframe in a specific section</p></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-slate-900 text-emerald-400 p-4 rounded-lg text-xs overflow-x-auto">{embedData?.iframe_code || 'Loading...'}</pre>
            <Button size="sm" variant="secondary" className="absolute top-2 right-2" onClick={() => copyCode(embedData?.iframe_code, 'iFrame code')} data-testid="copy-iframe-code">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Direct Widget URL</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input value={embedData?.widget_url || ''} readOnly className="flex-1 font-mono text-sm" data-testid="widget-url" />
            <Button variant="outline" onClick={() => copyCode(embedData?.widget_url, 'Widget URL')}><Copy className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
